import { compileFigure } from '../compiler.js';
import type { FigureSpec, ResolvedFigure } from '../model.js';
import { compileComparison, type ComparisonSpec } from '../primitives/comparison.js';
import { compileRangeNumberLine, type RangeNumberLineSpec } from '../primitives/range-number-line.js';
import { compileTimeline, type TimelineSpec } from '../primitives/timeline.js';

/**
 * A lesson is scenes; a scene is one claim, one piece of captured evidence, and
 * the one figure whose grammar happens to be the grammar of that claim. The
 * union below is the whole set of things a scene may draw with: the registered
 * lesson families, plus the three subject-agnostic primitives whose assertion
 * shapes (a measured axis, a decided comparison, a scaled elapsed rail) no
 * family carries.
 */
export type LessonFigureSpec = FigureSpec | ComparisonSpec | RangeNumberLineSpec | TimelineSpec;

/** The deep-lesson arc from design/content-architecture.md, plus its two working beats. */
export type LessonBeat =
  | 'symptom'
  | 'mechanism'
  | 'choice'
  | 'fix'
  | 'trace'
  | 'evidence'
  | 'trade-off'
  | 'production'
  | 'model';

export interface SceneSource {
  /** The instrument the text came out of, e.g. `main.go` or `go run -race .`. */
  caption: string;
  /** Verbatim from the capture. Never paraphrased, never reflowed. */
  text: string;
}

export interface TruthReference {
  /** File name inside kiln/content/truth. */
  file: string;
  /** `name` (blocks) or `id` (captures) of the capture inside that file. */
  capture: string;
}

export interface LessonScene {
  id: string;
  beat: LessonBeat;
  /** Uppercase stage label. Composition law: ≤32 characters, one line. */
  stageLabel: string;
  /** The claim of this scene. Composition law: ≤60 characters. */
  heading: string;
  /** Must add information the heading does not carry. Composition law: ≤90 characters. */
  subtitle: string;
  /** Why this figure and not another, recorded so the choice stays reviewable. */
  figureReason: string;
  code: SceneSource;
  evidence: SceneSource;
  /** The capture's own determinism note, verbatim. */
  determinism: string;
  truth: TruthReference;
  /**
   * Every string this scene quotes from the capture's output, including values
   * that reach the figure rather than the evidence card. Each one is checked
   * verbatim against the capture by test/go-lessons.test.ts.
   */
  quoted: string[];
  spec: (width: number) => LessonFigureSpec;
}

export interface GoLesson {
  id: string;
  module: string;
  title: string;
  premise: string;
  /** Go toolchain the captures were taken with. */
  toolchain: string;
  scenes: LessonScene[];
}

/** Compiles any lesson figure spec through its own registered compiler. */
export function compileLessonFigure(spec: LessonFigureSpec): ResolvedFigure {
  if (spec.family === 'comparison') return compileComparison(spec);
  if (spec.family === 'primitive-timeline') return compileTimeline(spec);
  if (spec.family === 'range-number-line') return compileRangeNumberLine(spec);
  return compileFigure(spec);
}

export const COMPOSITION_CAPS = {
  heading: 60,
  subtitle: 90,
  stageLabel: 32,
} as const;

/**
 * The composition law from design/composition.md, enforced at compile time so a
 * caption can never be promoted into a heading by accident.
 */
export function assertSceneComposition(lessonId: string, scene: LessonScene): void {
  const where = `${lessonId}/${scene.id}`;
  if (scene.heading.length > COMPOSITION_CAPS.heading) {
    throw new Error(`${where}: heading is ${scene.heading.length} characters, cap is ${COMPOSITION_CAPS.heading}`);
  }
  if (scene.subtitle.length > COMPOSITION_CAPS.subtitle) {
    throw new Error(`${where}: subtitle is ${scene.subtitle.length} characters, cap is ${COMPOSITION_CAPS.subtitle}`);
  }
  if (scene.stageLabel.length > COMPOSITION_CAPS.stageLabel) {
    throw new Error(`${where}: stage label is ${scene.stageLabel.length} characters, cap is ${COMPOSITION_CAPS.stageLabel}`);
  }
  if (scene.stageLabel !== scene.stageLabel.toUpperCase()) {
    throw new Error(`${where}: a stage label is a label, so it is uppercase`);
  }
  if (!scene.heading.trim() || !scene.subtitle.trim()) {
    throw new Error(`${where}: a scene needs both a claim and a line that adds to it`);
  }
  if (!scene.figureReason.trim()) throw new Error(`${where}: record why this figure was chosen`);
  if (!scene.quoted.length) throw new Error(`${where}: a scene must quote its capture`);
}

/** Every figure carries exactly one taught element: the law, checked structurally. */
export function taughtElementOf(spec: LessonFigureSpec): string | undefined {
  // A comparison names its taught element by marking one criterion decisive;
  // the compiler already refuses a spec with any other number of them.
  if (spec.family === 'comparison') return spec.criteria.find((criterion) => criterion.decisive)?.id;
  const record = spec as unknown as Record<string, unknown>;
  const keys = [
    'focusLineId',
    'focusSegmentId',
    'focusEventId',
    'focusFrameId',
    'focusStepId',
    'focusNodeId',
    'focusMarkId',
    'focusPartId',
    'focusRankId',
    'focusShellId',
    'focusCellId',
    'focusTupleId',
    'activeStageId',
  ];
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.length) return value;
  }
  const focus = record['focus'];
  return typeof focus === 'string' && focus !== 'overview' ? focus : undefined;
}
